/**
 * Live attendance per session (room entrance check-ins) — quorum at a glance.
 */

import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { parseLocalDate } from '@/models/event';
import { getAttendanceSummary } from '../checkinRepository';

export function SessionAttendanceCard({ eventId }: { eventId: string }) {
  const { data: summary } = useQuery({
    queryKey: ['attendance_summary', eventId],
    queryFn: () => getAttendanceSummary(eventId),
    refetchInterval: 30_000,
  });

  if (!summary || summary.sessions.length === 0) return null;
  const base = summary.registered_ministers || 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="size-5" /> Asistencia por sesión
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {summary.registered} inscritos · {summary.registered_ministers} ministros · {summary.event_attended} ingresaron al evento
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {summary.sessions.map((s) => (
          <div key={s.session_id} className="space-y-1">
            <div className="flex justify-between gap-2 text-sm">
              <span className="truncate">
                {s.name}{' '}
                <span className="text-muted-foreground">
                  · {parseLocalDate(s.session_date)?.toLocaleDateString('es-BO', { day: 'numeric', month: 'short' })}
                  {s.start_time && ` ${s.start_time.slice(0, 5)}`}
                </span>
              </span>
              <span className="shrink-0 font-medium">
                {s.attended_ministers} / {summary.registered_ministers} ministros
                <span className="text-muted-foreground"> ({s.attended} total)</span>
              </span>
            </div>
            <Progress value={(s.attended_ministers / base) * 100} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
